import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ChevronRight, Home, ArrowLeft } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';

const filterLabels = {
  'low_stock': 'Low Stock',
  'out_of_stock': 'Out of Stock',
  'expiring': 'Expiring Soon',
  'pending': 'Pending',
  'draft': 'Draft',
  'completed': 'Completed',
  'cancelled': 'Cancelled',
};

const routeLabels = {
  'dashboard': 'Dashboard',
  'sales': 'Sales & POS',
  'reports': 'Reports',
  'inventory': 'Inventory',
  'suppliers': 'Suppliers',
  'purchase-orders': 'Purchase Orders',
  'receiving': 'Receiving',
  'stock-transfers': 'Stock Transfers',
  'production': 'Production',
  'planning': 'Planning',
  'quality': 'Quality Control',
  'recipes': 'Recipes',
  'finance': 'Finance',
  'hr': 'Human Resources',
  'employees': 'Employees',
  'payroll': 'Payroll',
  'attendance': 'Attendance',
  'leave': 'Leave',
  'recruitment': 'Recruitment',
  'performance': 'Performance',
  'master-data': 'Master Data',
  'system': 'System Admin',
  'settings': 'Settings',
  'profile': 'Profile',
  'notifications': 'Notifications',
  'branches': 'Branches',
  'branch-comparison': 'Branch Analytics',
  'users': 'User Management',
  'executive-dashboard': 'Dashboard',
  'compliance': 'Compliance',
  'data-import-export': 'Import / Export',
  'sales-advanced': 'Returns & Loyalty',
  'pos-improvements': 'POS Operations',
  'inventory-advanced': 'ABC & Cycle Counts',
  'production-advanced': 'Equipment & HACCP',
  'hr-advanced': 'Overtime & Loans',
  'comprehensive': 'Comprehensive Report',
  'org-chart': 'Organization Chart',
  'time-attendance': 'Time & Attendance',
  'job-postings': 'Job Postings',
  'leave-management': 'Leave Management',
  'performance-management': 'Performance',
  'learning-development': 'Learning & Development',
  'employee-engagement': 'Employee Engagement',
  'ai-insights': 'AI Insights',
  'audit-trail': 'Audit Trail',
  'bulk-operations': 'Bulk Operations',
  'payroll-approval': 'Payroll Approval',
  'onboarding': 'Employee Onboarding',
  'production-dashboard': 'Production Dashboard',
  'production-requisitions': 'Requisitions',
  'production-planning': 'Production Planning',
  'quality-control': 'Quality Control',
  'super-admin': 'Super Admin',
  'discipline': 'Discipline',
  'document-management': 'Documents',
  'analytics-dashboard': 'HR Analytics',
};

const countModuleMap = {
  'inventory': 'inventory',
  'sales': 'sales',
  'production': 'production',
  'hr': 'hr',
  'finance': 'finance',
};

function Breadcrumb() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pathname = location.pathname;
  const [recordCount, setRecordCount] = useState(null);

  const firstSegment = pathname.split('/').filter(Boolean)[0];
  const moduleKey = countModuleMap[firstSegment];

  useEffect(() => {
    if (!moduleKey) { setRecordCount(null); return; }
    axios.get(`${API}/ux/nav-counts`).then(res => {
      const counts = res.data || {};
      if (moduleKey === 'inventory') setRecordCount(counts.inventory || null);
      else if (moduleKey === 'sales') setRecordCount(counts.today_sales || null);
      else setRecordCount(null);
    }).catch(() => setRecordCount(null));
  }, [moduleKey]);

  if (pathname === '/') return null;

  const segments = pathname.split('/').filter(Boolean);
  const crumbs = segments.map((segment, index) => {
    const path = '/' + segments.slice(0, index + 1).join('/');
    const label = routeLabels[segment] || segment.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const isLast = index === segments.length - 1;
    const showCount = index === 0 && recordCount !== null;
    return { path, label, isLast, showCount };
  });

  const drillDownFrom = location.state?.drillDownFrom;
  const drillDownPath = location.state?.drillDownPath;

  const activeFilter = searchParams.get('filter');
  const activeCategory = searchParams.get('category');
  const activeStatus = searchParams.get('status');
  const activeSearch = searchParams.get('search');
  const filterContext = activeFilter ? (filterLabels[activeFilter] || activeFilter.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())) : null;
  const categoryContext = activeCategory ? activeCategory.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null;
  const statusContext = activeStatus ? (filterLabels[activeStatus] || activeStatus.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())) : null;

  return (
    <div className="bg-white border-b border-slate-200">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-2">
        <nav className="flex items-center gap-2 text-sm flex-wrap" aria-label="Breadcrumb">
          <Link to="/" className="text-slate-400 hover:text-emerald-600 transition-colors flex items-center gap-1">
            <Home className="w-3.5 h-3.5" />
            <span>Home</span>
          </Link>

          {drillDownFrom && drillDownPath && (
            <>
              <ChevronRight className="w-3 h-3 text-slate-300" />
              <Link
                to={drillDownPath}
                className="text-emerald-500 hover:text-emerald-700 transition-colors flex items-center gap-1"
              >
                <ArrowLeft className="w-3 h-3" />
                <span>{drillDownFrom}</span>
              </Link>
            </>
          )}

          {crumbs.map((crumb) => (
            <span key={crumb.path} className="flex items-center gap-2">
              <ChevronRight className="w-3 h-3 text-slate-300" />
              {crumb.isLast ? (
                <span className="text-slate-700 font-medium">
                  {crumb.label}
                  {crumb.showCount && (
                    <span className="ml-1 text-xs text-slate-400 font-normal">({recordCount?.toLocaleString()})</span>
                  )}
                </span>
              ) : (
                <Link to={crumb.path} className="text-slate-400 hover:text-emerald-600 transition-colors">
                  {crumb.label}
                  {crumb.showCount && (
                    <span className="ml-1 text-xs text-slate-300">({recordCount?.toLocaleString()})</span>
                  )}
                </Link>
              )}
            </span>
          ))}

          {(filterContext || categoryContext || statusContext) && (
            <>
              <ChevronRight className="w-3 h-3 text-slate-300" />
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full border border-emerald-200">
                {filterContext || categoryContext || statusContext}
              </span>
            </>
          )}

          {activeSearch && (
            <>
              <ChevronRight className="w-3 h-3 text-slate-300" />
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                Search: {activeSearch}
              </span>
            </>
          )}
        </nav>
      </div>
    </div>
  );
}

export default Breadcrumb;
